import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RADIUS, useTheme } from '../../theme';
import { escapeHtml } from './html';
import type { MarkerType } from './planModel';

// Kartan i Planera jakt och i inbjudan (Leaflet, OSM). Ritar markgränsen, markens
// befintliga nålar (kan väljas), planens pass/poster och samlingsplatsen.

export const MARKER_COLOR: Record<MarkerType | 'meeting', string> = {
  pass: '#2f7d32', hund_start: '#c96a0c', samlingsplats: '#2b6cb0', lunch: '#6b6f63', fika: '#6b6f63', viltparad: '#6b6f63', meeting: '#2b6cb0',
};

export interface MapPin { id?: string; lat: number; lng: number; type: string; name?: string }
/** S1: markens såter på kartan. busy = text om såten redan är bokad den dagen. */
export interface MapSat { id: string; name: string; geometry: any; color: string; selected?: boolean; busy?: string | null }

export interface MapMarker { id: string; type: MarkerType; lat: number; lng: number; label: string; assignedName?: string; dim?: boolean; highlight?: boolean }

export function PlanMap({
  centerLat, centerLng, boundaryGeoJson, pins = [], markers = [], meeting, onMapClick, onPinClick, onMarkerClick, height = 340, cursor, ariaLabel,
  satar, onSatClick,
}: {
  satar?: MapSat[]; onSatClick?: (id: string) => void;
  centerLat?: number | null; centerLng?: number | null; boundaryGeoJson?: string | null;
  pins?: MapPin[]; markers?: MapMarker[]; meeting?: { lat: number; lng: number; label?: string } | null;
  onMapClick?: (lat: number, lng: number) => void; onPinClick?: (p: MapPin) => void; onMarkerClick?: (id: string) => void;
  height?: number | string; cursor?: string; ariaLabel?: string;
}) {
  const T = useTheme();
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layers = useRef<{ pins: L.LayerGroup; plan: L.LayerGroup; satar: L.LayerGroup; boundary: L.GeoJSON | null } | null>(null);
  const cb = useRef({ onMapClick, onPinClick, onMarkerClick, onSatClick });
  cb.current = { onMapClick, onPinClick, onMarkerClick, onSatClick };
  const fitted = useRef(false);
  const satarRef = useRef(satar);
  satarRef.current = satar;
  const fittedCount = useRef(0);

  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { zoomControl: true, attributionControl: true }).setView([centerLat || 62.5, centerLng || 15.5], centerLat ? 13 : 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(m);
    layers.current = { satar: L.layerGroup().addTo(m), pins: L.layerGroup().addTo(m), plan: L.layerGroup().addTo(m), boundary: null };
    m.on('click', (e: L.LeafletMouseEvent) => cb.current.onMapClick?.(e.latlng.lat, e.latlng.lng));
    map.current = m;
    const t = setTimeout(() => m.invalidateSize(), 120);
    return () => { clearTimeout(t); m.remove(); map.current = null; layers.current = null; };
  }, []);

  // Markgränsen
  useEffect(() => {
    const m = map.current, ls = layers.current;
    if (!m || !ls) return;
    ls.boundary?.remove();
    ls.boundary = null;
    if (!boundaryGeoJson) return;
    try {
      // Såterna ritas i eget lager när de skickas med (satar), annars som tunna streck här.
      const withSatar = !!satarRef.current;
      ls.boundary = L.geoJSON(JSON.parse(boundaryGeoJson), {
        filter: (f: any) => !(withSatar && f?.properties?.type === 'sub_area'),
        style: (f: any) => f?.properties?.type === 'sub_area'
          ? { color: '#00A2FF', weight: 1.5, opacity: 0.8, fill: false, dashArray: '4 4' }
          : { color: T.amber, weight: 2.5, opacity: 0.9, fill: false, dashArray: '7 4' },
        interactive: false,
      }).addTo(m);
      if (!fitted.current) { m.fitBounds(ls.boundary.getBounds(), { padding: [24, 24] }); fitted.current = true; }
    } catch { /* trasig GeoJSON */ }
  }, [boundaryGeoJson, T.amber, !!satar]);

  // Markens såter (S1): valda fylls, upptagna blir röda med vad som pågår, klick väljer.
  useEffect(() => {
    const ls = layers.current;
    if (!ls) return;
    ls.satar.clearLayers();
    for (const s of satar ?? []) {
      const color = s.busy ? '#e03131' : s.color;
      try {
        const layer = L.geoJSON({ type: 'Feature', properties: {}, geometry: s.geometry } as any, {
          style: { color, weight: s.selected ? 3 : 2, opacity: 0.95, fillColor: color, fillOpacity: s.selected ? 0.22 : s.busy ? 0.12 : 0.04, dashArray: s.selected ? undefined : '6 4' },
          interactive: !!cb.current.onSatClick,
        }).addTo(ls.satar);
        layer.bindTooltip(escapeHtml(s.busy ? `${s.name} · ${s.busy}` : s.name), { permanent: true, direction: 'center', className: 'qv-sat-label' });
        layer.on('click', e => { L.DomEvent.stopPropagation(e); cb.current.onSatClick?.(s.id); });
      } catch { /* trasig form */ }
    }
  }, [satar]);

  // Markens befintliga nålar (väljbara)
  useEffect(() => {
    const ls = layers.current;
    if (!ls) return;
    ls.pins.clearLayers();
    for (const p of pins) {
      if (!Number.isFinite(Number(p.lat)) || !Number.isFinite(Number(p.lng))) continue;
      const type = (p.type || '').toLowerCase();
      const color = (MARKER_COLOR as Record<string, string>)[type] ?? '#6b6f63';
      const icon = L.divIcon({ className: '', iconAnchor: [8, 8], html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};opacity:.55;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45)"></div>` });
      const mk = L.marker([Number(p.lat), Number(p.lng)], { icon, zIndexOffset: 50, keyboard: false }).addTo(ls.pins);
      mk.bindTooltip(escapeHtml(p.name || p.type), { direction: 'top', offset: [0, -6] });
      mk.on('click', e => { L.DomEvent.stopPropagation(e); cb.current.onPinClick?.(p); });
    }
    const m = map.current;
    if (m && !fitted.current && pins.length) {
      try { m.fitBounds(L.latLngBounds(pins.map(p => [Number(p.lat), Number(p.lng)] as [number, number])), { padding: [40, 40], maxZoom: 15 }); fitted.current = true; } catch { /* */ }
    }
  }, [pins]);

  // Planens poster och samlingsplatsen
  useEffect(() => {
    const ls = layers.current;
    if (!ls) return;
    ls.plan.clearLayers();
    for (const mk of markers) {
      const color = MARKER_COLOR[mk.type];
      const size = mk.highlight ? 30 : mk.assignedName ? 24 : 18;
      const ring = mk.highlight ? `box-shadow:0 0 0 4px ${T.amber},0 2px 6px rgba(0,0,0,.5)` : 'box-shadow:0 1px 4px rgba(0,0,0,.5)';
      const initials = mk.assignedName ? escapeHtml(mk.assignedName.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()) : '';
      const icon = L.divIcon({
        className: '', iconAnchor: [size / 2, size / 2],
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;${ring};opacity:${mk.dim ? 0.45 : 1};display:flex;align-items:center;justify-content:center;color:#fff;font:800 10px/1 system-ui">${initials}</div>`,
      });
      const marker = L.marker([mk.lat, mk.lng], { icon, zIndexOffset: mk.highlight ? 400 : 200 }).addTo(ls.plan);
      const tip = [mk.label, mk.assignedName].filter(Boolean).join(' · ');
      if (tip) marker.bindTooltip(escapeHtml(tip), mk.highlight ? { permanent: true, direction: 'right', offset: [size / 2, 0] } : { direction: 'bottom', offset: [0, size / 2] });
      marker.on('click', e => { L.DomEvent.stopPropagation(e); cb.current.onMarkerClick?.(mk.id); });
    }
    if (meeting) {
      const icon = L.divIcon({ className: '', iconAnchor: [14, 28], html: `<svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" fill="${MARKER_COLOR.meeting}" stroke="#fff" stroke-width="2"/><circle cx="12" cy="10" r="2.6" fill="#fff"/></svg>` });
      const mk = L.marker([meeting.lat, meeting.lng], { icon, zIndexOffset: 500 }).addTo(ls.plan);
      mk.bindTooltip(escapeHtml(meeting.label || 'Samlingsplats'), { permanent: true, direction: 'top', offset: [0, -26] });
    }
    // Kartor man ritar i zoomas bara första gången; visningskartor (inbjudan) följer med när punkter tillkommer.
    const m = map.current;
    const pts = [...markers.map(x => [x.lat, x.lng] as [number, number]), ...(meeting ? [[meeting.lat, meeting.lng] as [number, number]] : [])];
    const readOnly = !cb.current.onMapClick;
    if (m && pts.length && (!fitted.current || (readOnly && pts.length !== fittedCount.current))) {
      try { m.fitBounds(L.latLngBounds(pts), { padding: [32, 32], maxZoom: 15 }); fitted.current = true; fittedCount.current = pts.length; } catch { /* */ }
    }
  }, [markers, meeting, T.amber]);

  return (
    <div ref={el} role="application" aria-label={ariaLabel ?? 'Karta'}
      style={{ height, borderRadius: RADIUS.lg, border: `1px solid ${T.border}`, overflow: 'hidden', cursor: cursor ?? (onMapClick ? 'crosshair' : 'grab'), background: T.map, zIndex: 0, position: 'relative' }} />
  );
}
